/**
 * A moment in the recorded room, as the confirmed prototype draws it: inside the picture when the picture has the
 * room (below it when it does not), "{Host} paused for your answer" when it holds the talk, the line the host had
 * just said, the kind, the question, the one thing to do, and Skip. Once answered it thanks them and the talk carries
 * on by itself once the thanks has had its two seconds (`holdsTalk`).
 *
 * It only ever collects: nothing about who else answered is ever drawn here.
 */
import { type ReactNode, useEffect, useRef, useState } from "react";
import type {
  HandoutConfig,
  PollConfig,
  QuestionConfig,
  ReactionConfig,
  WatchInteraction,
} from "../../../shared/types";
import { assetUrl } from "../../lib/api";
import type { AnswerFn } from "../TalkPlayer";
import { GIcon, type GIconName } from "./GIcon";
import { mmss } from "./talk";

/** The longest answer a question takes. */
export const ANSWER_MAX = 200;

/** What the held picture says, kind by kind. */
export function heldWords(type: WatchInteraction["type"], host: string): string {
  if (type === "reaction") return `${host} paused so you can react`;
  if (type === "handout") return `${host} paused to hand you this`;
  return `${host} paused for your answer`;
}

function kindOf(type: WatchInteraction["type"], host: string): [GIconName, string] {
  if (type === "poll") return ["poll", "A quick poll"];
  if (type === "question") return ["quest", "A quick question"];
  if (type === "reaction") return ["heart", `${host}’s moment`];
  return ["file", `A handout from ${host}`];
}

function askOf(m: WatchInteraction): string {
  if (m.type === "poll") return (m.config as PollConfig).question ?? "";
  if (m.type === "question") return (m.config as QuestionConfig).question ?? "";
  if (m.type === "reaction") return (m.config as ReactionConfig).prompt ?? "";
  return (m.config as HandoutConfig).title ?? "";
}

export function RoomMoment({
  moment,
  held,
  inside,
  takesKeys,
  host,
  hostAvatar,
  said,
  onAnswer,
  onSkip,
  onFloat,
}: {
  moment: WatchInteraction;
  /** Does this moment hold the talk? */
  held: boolean;
  /** In the picture, or below it. */
  inside: boolean;
  /** On a laptop or full screen the card takes the keyboard as it arrives. */
  takesKeys: boolean;
  host: string;
  hostAvatar: string | null;
  /** The line the host said just before, when the talk's words are known. */
  said: string | null;
  onAnswer: AnswerFn;
  onSkip: (id: string) => void;
  /** A reaction floats up over the picture. */
  onFloat: (emoji: string) => void;
}): ReactNode {
  const box = useRef<HTMLDivElement>(null);
  const [icon, label] = kindOf(moment.type, host);
  const mine = moment.my_response;

  // The card takes the keyboard when it arrives (only then), so Escape and Tab work at once. Why not on a phone or
  // tablet: a focused answer box would throw the keyboard up over the card before they chose to write.
  useEffect(() => {
    if (!takesKeys) return;
    const first = box.current?.querySelector<HTMLElement>(".mo-opt, textarea, [data-emo], .btn");
    first?.focus({ preventScroll: true });
  }, [moment.id]);

  const thanks = (words: ReactNode) => (
    <p className="mo-thanks">
      <GIcon name="check" size={20} />
      <span>
        {words}
        {held ? <em>The talk carries on from {mmss(moment.appears_at)}.</em> : null}
      </span>
    </p>
  );
  const footer = (note: string) => (
    <div className="mo-foot">
      <span>{note}</span>
      <button className="mo-skip" type="button" onClick={() => onSkip(moment.id)}>
        Skip <GIcon name="chr" size={16} />
      </button>
    </div>
  );

  let body: ReactNode = null;
  if (moment.type === "poll") {
    const picked = mine?.kind === "poll" ? mine.option : null;
    body = (
      <>
        <div className="mo-opts">
          {((moment.config as PollConfig).options ?? []).map((o) => (
            <button
              key={o.label}
              className={picked === o.label ? "mo-opt on" : "mo-opt"}
              type="button"
              disabled={picked !== null}
              onClick={() => onAnswer(moment.id, { kind: "poll", option: o.label })}
            >
              <span className="rad" />
              {o.label}
            </button>
          ))}
        </div>
        {picked !== null
          ? thanks(<>Thanks, got it. You said <b>{picked}</b>. Only <bdi>{host}</bdi> sees this.</>)
          : footer(`Only ${host} sees your answer.`)}
      </>
    );
  } else if (moment.type === "question") {
    body = <QuestionBody moment={moment} host={host} onAnswer={onAnswer} thanks={thanks} footer={footer} />;
  } else if (moment.type === "reaction") {
    const reacted = mine?.kind === "reaction" ? mine.emoji : null;
    body = (
      <>
        <div className="mo-emo">
          {((moment.config as ReactionConfig).reactions ?? []).map((r) => (
            <button
              key={r.emoji}
              type="button"
              data-emo={r.emoji}
              className={reacted === r.emoji ? "on" : undefined}
              aria-label={`React ${r.emoji}`}
              disabled={reacted !== null}
              onClick={() => {
                onFloat(r.emoji);
                onAnswer(moment.id, { kind: "reaction", emoji: r.emoji });
              }}
            >
              {r.emoji}
            </button>
          ))}
        </div>
        {reacted !== null ? thanks(<><bdi>{host}</bdi> will see you felt that.</>) : footer(`Tap one. Only ${host} sees it.`)}
      </>
    );
  } else {
    const cfg = moment.config as HandoutConfig;
    const saved = mine?.kind === "handout";
    const meta = ["PDF", (cfg.size ?? "").trim()].filter(Boolean).join(" · ");
    const take = () => onAnswer(moment.id, { kind: "handout", downloaded: true });
    body = (
      <>
        <div className="file">
          <span className="file-ic">PDF</span>
          <div>
            <b>{cfg.file}</b>
            <span>{meta}</span>
          </div>
          {cfg.key ? (
            <a className={saved ? "btn done" : "btn btn-p"} href={assetUrl(cfg.key)} download={cfg.file} target="_blank" rel="noreferrer" onClick={take}>
              <GIcon name={saved ? "check" : "down"} size={16} /> {saved ? "Saved" : "Download"}
            </a>
          ) : (
            <button className={saved ? "btn done" : "btn btn-p"} type="button" disabled={saved} onClick={take}>
              <GIcon name={saved ? "check" : "down"} size={16} /> {saved ? "Saved" : "Download"}
            </button>
          )}
        </div>
        {saved ? thanks(<>Saved. It’s on your page too.</>) : footer("It stays on your page too.")}
      </>
    );
  }

  return (
    <div className={inside ? "rm-mo on in" : "rm-mo on below"} role="dialog" aria-live="polite" aria-label="A moment from the talk" ref={box}>
      <div className="mo">
        {held ? (
          <div className="mo-held">
            {hostAvatar ? <img className="av" src={hostAvatar} alt="" /> : null}
            <GIcon name="pause" size={13} />
            <span>{heldWords(moment.type, host)}</span>
          </div>
        ) : null}
        {said ? <p className="mo-said">“{said}”</p> : null}
        <p className="mo-k">
          <GIcon name={icon} size={15} /> {label}
        </p>
        <h3>{askOf(moment)}</h3>
        {body}
      </div>
    </div>
  );
}

function QuestionBody({
  moment,
  host,
  onAnswer,
  thanks,
  footer,
}: {
  moment: WatchInteraction;
  host: string;
  onAnswer: AnswerFn;
  thanks: (words: ReactNode) => ReactNode;
  footer: (note: string) => ReactNode;
}): ReactNode {
  const [text, setText] = useState("");
  const mine = moment.my_response;
  // Once sent, the box keeps their words beside the thanks, as the prototype leaves them; a send that fails keeps them
  // too, ready to go again.
  const sent = mine?.kind === "question" ? mine.text : null;
  const send = () => {
    const v = text.trim();
    if (!v) return;
    onAnswer(moment.id, { kind: "question", text: v });
  };
  const sub = (moment.config as QuestionConfig).sub;
  return (
    <>
      <textarea
        className="mo-ta"
        dir="auto"
        maxLength={ANSWER_MAX}
        placeholder={sub || "Type your answer"}
        aria-label="Your answer"
        value={sent ?? text}
        disabled={sent !== null}
        onChange={(e) => setText(e.target.value.slice(0, ANSWER_MAX))}
        onKeyDown={(e) => {
          if (e.key !== "Enter" || e.shiftKey) return;
          e.preventDefault();
          send();
        }}
      />
      {sent !== null ? null : (
        <div className="mo-row">
          <span className="mo-cnt tnum">{`${text.length} / ${ANSWER_MAX}`}</span>
          <button className="btn btn-p btn-m" type="button" disabled={!text.trim()} onClick={send}>
            Send to {host}
          </button>
        </div>
      )}
      {sent !== null ? thanks(<>Sent. <bdi>{host}</bdi> answers on your page.</>) : footer(`Only ${host} sees your answer.`)}
    </>
  );
}
